import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { EvaSuggestedPromptsComponent } from 'src/app/eva-sidebar/eva-suggested-prompts/eva-suggested-prompts.component';
import { EvaQuestionAnswerService } from 'src/app/eva-sidebar/shared/services/eva-question-answer.service';

export interface EvaPromptSubmittedEvent {
  prompt: string;
}

@Component({
  selector: 'ez-eva-prompt',
  templateUrl: './eva-prompt.component.html',
  styleUrls: ['./eva-prompt.component.scss'],
  imports: [
    CdkTextareaAutosize,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    EvaSuggestedPromptsComponent,
  ],
})
export class EvaPromptComponent {

  protected readonly inConversation = input(false);
  protected readonly isCallingApi = input(false);
  /** A HITL confirmation is pending — disables the input until the user selects (EZ-186935). */
  protected readonly hasPendingAction = input(false);
  /** A confirmed action is processing — suppresses Stop Response + new questions (EZ-186935 AC4). */
  protected readonly isProcessingAction = input(false);
  protected readonly showStaticPrompts = input(false);
  /** Static prompts to hide, e.g. ones the current page's own prompts duplicate. */
  protected readonly excludedStaticPrompts = input<readonly string[]>([]);

  protected readonly suggestedPrompts = input<string[]>([]);
  protected readonly promptSubmitted = output<EvaPromptSubmittedEvent>();
  /** Re-emitted to the parent, which owns applicant resolution for the seed prompt. */
  protected readonly suggestedPromptSelected = output<string>();
  protected readonly promptTrigger = viewChild(MatAutocompleteTrigger);
  /** What the agent has typed, for the panel's filter. The raw field is ES-private. */
  protected readonly typedQuery = computed(() => this.#evaPromptValue()?.trim() ?? '');

  protected submitting = signal(false);

  protected readonly form = new FormGroup({
    evaPrompt: new FormControl(''),
  });

  readonly #evaQuestionAnswerService = inject(EvaQuestionAnswerService);

  readonly #evaPromptValue = toSignal(this.form.controls.evaPrompt.valueChanges, { initialValue: '' });

  protected placeholderText = computed(() => {
    return this.inConversation()
      ? 'What else can I help with?'
      : 'I can help you find information on clients, policies, step-by-step instructions, and more.';
  });

  protected readonly sendDisabled = computed(() =>
    this.hasPendingAction()
    || this.isProcessingAction()
    || !this.isCallingApi() && (!this.#evaPromptValue()?.trim() || this.submitting())
  );

  constructor() {
    // Disable the input while a confirmation is pending or processing (EZ-186935 AC2.2 / AC3 / AC4).
    effect(() => {
      const blocked = this.hasPendingAction() || this.isProcessingAction();
      const control = this.form.controls.evaPrompt;
      if (blocked && control.enabled) {
        control.disable({ emitEvent: false });
      } else if (!blocked && control.disabled) {
        control.enable({ emitEvent: false });
      }
    });

    // Closing here rather than in submit() is deliberate: submit() is only one of several paths
    // that send a question — onApplicantSelected, onPolicySelected, onActionOptionSelected and the
    // onSendQuestion$ subscriber all call processQuery directly, without going through this
    // component. isCallingApi is the one signal every path sets, so keying the close to it makes
    // the effect below the only route back to an open panel, whichever way the question was sent.
    effect(() => {
      if (this.isCallingApi()) {
        this.promptTrigger()?.closePanel();
      }
    });

  }

  protected handlePromptAction(): void {
    if (this.isProcessingAction()) {
      return; // Stop Response unavailable while a confirmed action executes (AC4).
    }
    if (this.isCallingApi()) {
      this.#evaQuestionAnswerService.abort();
    } else {
      this.submit();
    }
  }

  protected onSuggestedPromptSelected(prompt: string): void {
    if (this.isCallingApi() || this.hasPendingAction() || this.isProcessingAction()) {
      return;
    }
    // The panel is bound to the form control, so Material writes the option into the textarea.
    // Without this the typed filter text would survive the send.
    this.form.reset();
    this.suggestedPromptSelected.emit(prompt);
  }

  protected submit(event?: Event): void {
    if (this.isCallingApi() || this.hasPendingAction() || this.isProcessingAction()) {
      return;
    }
    event?.preventDefault();
    this.#evaQuestionAnswerService.abort();
    const prompt = this.form.value.evaPrompt?.trim();
    if (!prompt || this.submitting()) {
      return;
    }
    this.submitting.set(true);
    this.promptSubmitted.emit({ prompt });
    this.form.reset();
    this.submitting.set(false);
  }
}
